import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Divider,
	notification,
	Spin,
	Badge,
	Tooltip,
	Popconfirm,
	Modal,
	Form,
	Input,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiCreditCardDuotone,
	PiCurrencyDollarDuotone,
	PiCalendarDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiInfoDuotone,
	PiCheckCircleDuotone,
	PiPlayDuotone,
	PiWarningDuotone,
} from "react-icons/pi";
import { requestGetTransactionById, requestProcessPayment } from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { TextArea } = Input;

function TransactionViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [processPaymentLoading, setProcessPaymentLoading] = useState(false);
	const [transaction, setTransaction] = useState(null);
	const [processModalVisible, setProcessModalVisible] = useState(false);
	const [processForm] = Form.useForm();

	useEffect(() => {
		document.title = "Cloudiva Console | Transaction Details";
		fetchTransactionDetails();
	}, [id]);

	const fetchTransactionDetails = () => {
		setLoading(true);
		requestGetTransactionById(id)
			.then((response) => {
				const transactionData = response.data.data || response.data;
				setTransaction(transactionData);
			})
			.catch((error) => {
				console.error("Error fetching transaction details:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch transaction details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleProcessPayment = () => {
		setProcessModalVisible(true);
		processForm.resetFields();
	};

	const handleProcessPaymentSubmit = (values) => {
		setProcessPaymentLoading(true);
		const payload = {
			notes: values.notes || "",
		};

		requestProcessPayment(id, payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Payment processed successfully",
				});
				setProcessModalVisible(false);
				processForm.resetFields();
				// Refresh transaction details
				fetchTransactionDetails();
			})
			.catch((error) => {
				console.error("Error processing payment:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to process payment",
				});
			})
			.finally(() => {
				setProcessPaymentLoading(false);
			});
	};

	const handleProcessPaymentCancel = () => {
		setProcessModalVisible(false);
		processForm.resetFields();
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "PROCESSING":
				return "blue";
			case "COMPLETED":
			case "SUCCESS":
				return "green";
			case "FAILED":
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "PROCESSING":
				return "Processing";
			case "COMPLETED":
			case "SUCCESS":
				return "Completed";
			case "FAILED":
				return "Failed";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const getCategoryColor = (category) => {
		switch (category) {
			case "PAYMENT":
				return "green";
			case "REFUND":
				return "red";
			case "ADJUSTMENT":
				return "blue";
			default:
				return "default";
		}
	};

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!transaction) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Text>Transaction not found</Text>
				</div>
			</div>
		);
	}

	const canProcessPayment = transaction.status === "PENDING" && transaction.category === "PAYMENT";

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/transactions")} />
						<Title level={4} style={{ margin: 0 }}>
							Transaction Details
						</Title>
						<Tag color={getStatusColor(transaction.status)}>
							{getStatusText(transaction.status)}
						</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Row gutter={24}>
					{/* Transaction Information */}
					<Col span={16}>
						<Card
							title={
								<Space>
									<PiCreditCardDuotone size={20} />
									<span>Transaction Information</span>
								</Space>
							}
							style={{ marginBottom: 24 }}
							extra={
								<Space>
									{canProcessPayment && (
										<Popconfirm
											title="Process Payment"
											description="Are you sure you want to process this payment? This action cannot be undone."
											onConfirm={handleProcessPayment}
											okText="Yes, Process"
											cancelText="Cancel">
											<Button
												type="primary"
												icon={<PiPlayDuotone />}
												loading={processPaymentLoading}>
												Process Payment
											</Button>
										</Popconfirm>
									)}
								</Space>
							}>
							<Descriptions column={2} bordered>
								<Descriptions.Item label="Serial Number" span={1}>
									<Text strong>{transaction.number}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Status" span={1}>
									<Tag color={getStatusColor(transaction.status)}>
										{getStatusText(transaction.status)}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Category" span={1}>
									<Tag color={getCategoryColor(transaction.category)}>{transaction.category}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Payment Method" span={1}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiCreditCardDuotone size={16} style={{ marginRight: 8 }} />
										{`${transaction.method?.replace("_", " ")} (${transaction.channel?.replace(
											"_",
											" "
										)})` || "N/A"}
									</div>
								</Descriptions.Item>
								<Descriptions.Item label="Amount" span={1}>
									<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
										{formatCurrency(transaction.amount, transaction.currency)}
									</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Currency" span={1}>
									<Tag color="blue">{transaction.currency}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Created Date" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(transaction.createdAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label="Last Updated" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(transaction.updatedAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								{transaction.processedAt && (
									<Descriptions.Item label="Processed Date" span={2}>
										<Space>
											<PiCheckCircleDuotone style={{ color: "#52c41a" }} />
											{dayjs(transaction.processedAt).format("DD MMMM YYYY, HH:mm")}
										</Space>
									</Descriptions.Item>
								)}
								<Descriptions.Item label="Transaction ID" span={2}>
									<Text code style={{ fontSize: "12px" }}>
										{transaction._id}
									</Text>
								</Descriptions.Item>
							</Descriptions>
						</Card>

						{/* Related Records */}
						<Card
							title={
								<Space>
									<PiReceiptDuotone size={20} />
									<span>Related Records</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Row gutter={16}>
								{transaction.invoiceId && (
									<Col span={12}>
										<div
											style={{
												border: "1px solid #f0f0f0",
												borderRadius: "8px",
												padding: "16px",
												backgroundColor: "#fafafa",
											}}>
											<div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
												<PiReceiptDuotone size={20} style={{ marginRight: 8, color: "#52c41a" }} />
												<Text strong>Related Invoice</Text>
											</div>
											{transaction.metadata?.invoiceNumber && (
												<div style={{ marginBottom: 8 }}>
													<Text>Number: </Text>
													<Button
														type="link"
														style={{ padding: 0, height: "auto" }}
														onClick={() => navigate(`/invoices/${transaction.invoiceId}`)}>
														{transaction.metadata.invoiceNumber}
													</Button>
												</div>
											)}
											<div style={{ fontSize: "12px", color: "#666" }}>
												Invoice ID: {transaction.invoiceId}
											</div>
										</div>
									</Col>
								)}
								{transaction.team && (
									<Col span={12}>
										<div
											style={{
												border: "1px solid #f0f0f0",
												borderRadius: "8px",
												padding: "16px",
												backgroundColor: "#fafafa",
											}}>
											<div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
												<PiUserDuotone size={20} style={{ marginRight: 8, color: "#1890ff" }} />
												<Text strong>Team</Text>
											</div>
											<div style={{ marginBottom: 4 }}>
												<Text>{transaction.team.name}</Text>
											</div>
											{transaction.team.currency && (
												<div>
													<Tag color="green">{transaction.team.currency}</Tag>
												</div>
											)}
											<div style={{ fontSize: "12px", color: "#666", marginTop: 4 }}>
												Team ID: {transaction.teamId}
											</div>
										</div>
									</Col>
								)}
							</Row>
						</Card>
					</Col>

					{/* Transaction Status & Additional Info */}
					<Col span={8}>
						{/* Status Information */}
						<Card
							title={
								<Space>
									<PiCheckCircleDuotone size={20} />
									<span>Status Information</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ textAlign: "center", padding: "20px 0" }}>
								<Tag
									color={getStatusColor(transaction.status)}
									style={{ fontSize: "16px", padding: "12px 24px" }}>
									{getStatusText(transaction.status)}
								</Tag>
								<div style={{ marginTop: 16, fontSize: "12px", color: "#666" }}>
									{transaction.status === "PENDING" && "Waiting for payment processing"}
									{transaction.status === "PROCESSING" && "Payment is being processed"}
									{transaction.status === "COMPLETED" &&
										transaction.processedAt &&
										`Completed on ${dayjs(transaction.processedAt).format("DD MMM YYYY, HH:mm")}`}
									{transaction.status === "FAILED" && "Payment processing failed"}
									{transaction.status === "CANCELLED" && "Transaction was cancelled"}
								</div>
								{canProcessPayment && (
									<div
										style={{
											marginTop: 16,
											padding: "12px",
											backgroundColor: "#fff7e6",
											borderRadius: "6px",
											border: "1px solid #ffd591",
										}}>
										<div style={{ fontSize: "12px", color: "#ad6800", textAlign: "left" }}>
											<PiWarningDuotone style={{ marginRight: 4 }} />
											This payment is ready to be processed
										</div>
									</div>
								)}
							</div>
						</Card>

						{/* Financial Summary */}
						<Card
							title={
								<Space>
									<PiCurrencyDollarDuotone size={20} />
									<span>Financial Summary</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<Text>Transaction Amount:</Text>
									<Text strong style={{ fontSize: "18px", color: "#1890ff" }}>
										{formatCurrency(transaction.amount, transaction.currency)}
									</Text>
								</div>
								<Divider style={{ margin: "8px 0" }} />
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<Text>Category:</Text>
									<Tag color={getCategoryColor(transaction.category)}>{transaction.category}</Tag>
								</div>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<Text>Currency:</Text>
									<Tag color="blue">{transaction.currency}</Tag>
								</div>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<Text>Payment Method:</Text>
									<Text>
										{`${transaction.method?.replace("_", " ")} (${transaction.channel?.replace(
											"_",
											" "
										)})` || "N/A"}
									</Text>
								</div>
							</div>
						</Card>

						{/* Metadata */}
						{transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
							<Card
								title={
									<Space>
										<PiInfoDuotone size={20} />
										<span>Additional Information</span>
									</Space>
								}>
								<div style={{ backgroundColor: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
									<pre style={{ margin: 0, fontSize: "12px" }}>
										{JSON.stringify(transaction.metadata, null, 2)}
									</pre>
								</div>
							</Card>
						)}
					</Col>
				</Row>
			</div>

			{/* Process Payment Modal */}
			<Modal
				title={
					<Space>
						<PiPlayDuotone />
						<span>Process Payment</span>
					</Space>
				}
				open={processModalVisible}
				onCancel={handleProcessPaymentCancel}
				footer={null}
				width={500}>
				<div style={{ marginBottom: 16 }}>
					<div
						style={{
							padding: "12px",
							backgroundColor: "#fff7e6",
							borderRadius: "6px",
							border: "1px solid #ffd591",
							marginBottom: 16,
						}}>
						<div style={{ fontSize: "14px", color: "#ad6800" }}>
							<PiWarningDuotone style={{ marginRight: 8 }} />
							<strong>Warning:</strong> This action will mark the transaction as completed and
							cannot be undone.
						</div>
					</div>
					<Descriptions size="small" column={1} bordered>
						<Descriptions.Item label="Transaction">{transaction.number}</Descriptions.Item>
						<Descriptions.Item label="Amount">
							{formatCurrency(transaction.amount, transaction.currency)}
						</Descriptions.Item>
						<Descriptions.Item label="Payment Method">
							{`${transaction.method?.replace("_", " ")} (${transaction.channel?.replace(
								"_",
								" "
							)})` || "N/A"}
						</Descriptions.Item>
					</Descriptions>
				</div>

				<Form
					form={processForm}
					layout="vertical"
					onFinish={handleProcessPaymentSubmit}
					style={{ marginTop: 16 }}>
					<Form.Item label="Processing Notes (Optional)" name="notes">
						<TextArea rows={3} placeholder="Add any notes about the payment processing..." />
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleProcessPaymentCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={processPaymentLoading} danger>
								Process Payment
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default TransactionViewPage;
